import {
    Component,
    OnDestroy,
    OnInit,
    Optional,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NearbyMapComponent } from '../nearby-map.component';
import { CommonService } from '../../shareModule/services/common.service';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-customization-colors',
    templateUrl: './customization-colors.component.html',
    styleUrls: ['./customization-colors.component.scss'],
})
export class CustomizationColorsComponent implements OnInit, OnDestroy {
    @ViewChild('colorPickerModal', { static: true })
    colorPickerModal: TemplateRef<any>;

    selectedMapSideColor: string = '#ffffff';
    selectedMapHeaderColor: string = '#ffffff';
    selectedPopupLeaderColor: string = '#ffffff';
    selectedFaqQuestionFieldColor: string = '#ffffff';
    
    // Default colors
    private defaultMapSideColor: string = '#ffffff';
    private defaultMapHeaderColor: string = '#592880';
    private defaultPopupLeaderColor: string = '#592880';
    private defaultFaqQuestionFieldColor: string = '#592880';
    
    private modalRef?: NgbModalRef;
    private mapCustomizationSub?: Subscription;
    isSaving = false;

    constructor(
        private modalService: NgbModal,
        private commonService: CommonService,
        private toastr: ToastrService,
        @Optional() private nearbyMapComponent: NearbyMapComponent
    ) { }

    ngOnInit(): void {
        this.syncWithParentColors();
        this.subscribeToMapCustomization();
    }

    ngOnDestroy(): void {
        this.mapCustomizationSub?.unsubscribe();
    }

    openModal(): void {
        this.syncWithParentColors();
        this.modalRef = this.modalService.open(this.colorPickerModal, {
            centered: true,
            size: 'md',
        });
    }

    handleMapSideColorInput(value: string): void {
        this.selectedMapSideColor = value;
    }

    handleMapHeaderColorInput(value: string): void {
        this.selectedMapHeaderColor = value;
    }

    handlePopupLeaderColorInput(value: string): void {
        this.selectedPopupLeaderColor = value;
    }

    handleFaqQuestionFieldColorInput(value: string): void {
        this.selectedFaqQuestionFieldColor = value;
    }

    handleMapSideHexInput(value: string): void {
        const normalized = this.normalizeHexInput(value);
        if (normalized) {
            this.selectedMapSideColor = normalized;
        }
    }

    handleMapHeaderHexInput(value: string): void {
        const normalized = this.normalizeHexInput(value);
        if (normalized) {
            this.selectedMapHeaderColor = normalized;
        }
    }

    handlePopupLeaderHexInput(value: string): void {
        const normalized = this.normalizeHexInput(value);
        if (normalized) {
            this.selectedPopupLeaderColor = normalized;
        }
    }

    handleFaqQuestionFieldHexInput(value: string): void {
        const normalized = this.normalizeHexInput(value);
        if (normalized) {
            this.selectedFaqQuestionFieldColor = normalized;
        }
    }

    resetToDefaults(): void {
        this.selectedMapSideColor = this.defaultMapSideColor;
        this.selectedMapHeaderColor = this.defaultMapHeaderColor;
        this.selectedPopupLeaderColor = this.defaultPopupLeaderColor;
        this.selectedFaqQuestionFieldColor = this.defaultFaqQuestionFieldColor;
    }

    private normalizeHexInput(value: string): string | null {
        if (!value) return null;
        
        // Remove any whitespace
        value = value.trim();
        
        // Add # if missing
        if (!value.startsWith('#')) {
            value = '#' + value;
        }
        
        // Validate hex format
        if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)) {
            // Convert 3-digit hex to 6-digit
            if (value.length === 4) {
                value = '#' + value[1] + value[1] + value[2] + value[2] + value[3] + value[3];
            }
            return value.toLowerCase();
        }
        
        return null;
    }

    confirmSelection(): void {
        if (this.isSaving) {
            return;
        }
        const payload = {
            filter: this.getFilter(),
            mapSideColor: this.selectedMapSideColor,
            mapHeaderColor: this.selectedMapHeaderColor,
            popupLeaderColor: this.selectedPopupLeaderColor,
            faqQuestionFieldColor: this.selectedFaqQuestionFieldColor,
        };
        this.isSaving = true;
        this.commonService.saveMapCustomization(payload).subscribe({
            next: () => {
                this.commonService.mapCustomization = payload;
                this.commonService.mapCustomizationObserver.next(payload);
                this.toastr.success('Map customization saved');
                this.isSaving = false;
                this.modalRef?.close();
            },
            error: (error) => {
                this.toastr.error(
                    error?.error?.message || 'Failed to save customization'
                );
                this.isSaving = false;
            },
        });
    }

    closeModal(): void {
        this.modalRef?.dismiss();
    }

    private normalizeColor(color?: string): string {
        if (!color || typeof color !== 'string') {
            return '#592880';
        }
        
        const trimmed = color.trim();
        
        // Handle hex colors
        if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) {
            // Convert 3-digit hex to 6-digit
            if (trimmed.length === 4) {
                return '#' + trimmed[1] + trimmed[1] + trimmed[2] + trimmed[2] + trimmed[3] + trimmed[3];
            }
            return trimmed.toLowerCase();
        }
        
        // Handle CSS variables (e.g., var(--white), var(--primary))
        if (trimmed.startsWith('var(--')) {
            if (trimmed.includes('white')) {
                return '#ffffff';
            }
            if (trimmed.includes('primary')) {
                return '#592880';
            }
        }
        
        // Handle common color names
        const colorMap: { [key: string]: string } = {
            'white': '#ffffff',
            'black': '#000000',
            'red': '#ff0000',
            'green': '#00ff00',
            'blue': '#0000ff',
        };
        
        if (colorMap[trimmed.toLowerCase()]) {
            return colorMap[trimmed.toLowerCase()];
        }

        return '#592880';
    }

    private syncWithParentColors(): void {
        const customization = this.commonService.mapCustomization;
        if (customization && customization.filter === this.getFilter()) {
            this.selectedMapSideColor = this.normalizeColor(
                customization.mapSideColor ?? this.defaultMapSideColor
            );
            this.selectedMapHeaderColor = this.normalizeColor(
                customization.mapHeaderColor ?? this.defaultMapHeaderColor
            );
            this.selectedPopupLeaderColor = this.normalizeColor(
                customization.popupLeaderColor ?? this.defaultPopupLeaderColor
            );
            this.selectedFaqQuestionFieldColor = this.normalizeColor(
                customization.faqQuestionFieldColor ?? this.defaultFaqQuestionFieldColor
            );
        } else {
            this.selectedMapSideColor = this.defaultMapSideColor;
            this.selectedMapHeaderColor = this.defaultMapHeaderColor;
            this.selectedPopupLeaderColor = this.defaultPopupLeaderColor;
            this.selectedFaqQuestionFieldColor = this.defaultFaqQuestionFieldColor;
        }
    }

    private subscribeToMapCustomization(): void {
        this.mapCustomizationSub =
            this.commonService.mapCustomizationObserver.subscribe(
                (customization) => {
                    if (
                        !customization ||
                        (customization.filter &&
                            customization.filter !== this.getFilter())
                    ) {
                        this.syncWithParentColors();
                        return;
                    }
                    this.applyCustomization(customization);
                }
            );
    }

    private getFilter(): string {
        return this.nearbyMapComponent?.comicChatFilter ?? 'map';
    }

    private applyCustomization(customization: any): void {
        this.selectedMapSideColor = this.normalizeColor(
            customization?.mapSideColor ?? this.defaultMapSideColor
        );
        this.selectedMapHeaderColor = this.normalizeColor(
            customization?.mapHeaderColor ?? this.defaultMapHeaderColor
        );
        this.selectedPopupLeaderColor = this.normalizeColor(
            customization?.popupLeaderColor ?? this.defaultPopupLeaderColor
        );
        this.selectedFaqQuestionFieldColor = this.normalizeColor(
            customization?.faqQuestionFieldColor ?? this.defaultFaqQuestionFieldColor
        );
    }
}

